/**
 * SMS Triggers Type Definitions
 * 
 * Comprehensive type definitions for SMS trigger functionality
 */

// Enums for trigger types and operators
// Simplified to two types after backend unification
export type TriggerType = 'milestone' | 're_engagement';

export type TriggerEventType = 
  | 'order_created'
  | 'order_delivered'
  | 'customer_milestone_reached'
  | 'customer_inactive_detected';

export type ConditionOperator = 
  | 'equals' 
  | 'not_equals'
  | 'greater_than' 
  | 'less_than' 
  | 'greater_than_or_equal'
  | 'less_than_or_equal'
  | 'between'
  | 'in'
  | 'not_in'
  | 'contains'
  | 'not_contains';

export type ConditionField = 
  | 'total_orders'
  | 'total_spend'
  | 'average_order_value'
  | 'days_since_last_order'
  | 'service_type'
  | 'loyalty_tier';

export type LogicOperator = 'AND' | 'OR';

export type TimeUnit = 'minutes' | 'hours' | 'days' | 'weeks';

export type TriggerStatus = 'active' | 'paused' | 'draft' | 'expired';

export type ExecutionStatus = 
  | 'pending'
  | 'scheduled'
  | 'sent'
  | 'failed'
  | 'skipped'
  | 'cancelled';

// Database trigger types (what's actually stored in the database)
export type DatabaseTriggerType = 
  | 'customer_activity_update'
  | 'order_confirmed'
  | 'nth_order'
  | 'first_order'
  | 'spending_milestone'
  | 'customer_inactive';

// Main trigger rule interface matching database schema
export interface SmsTriggerRule {
  id: string;
  restaurant_id: string;
  organisation_id: string;
  rule_name: string;
  rule_description?: string;
  trigger_type: DatabaseTriggerType | TriggerType; // Can be either database or form type
  rule_conditions: TriggerRuleConditions;
  time_delay: TimeDelay;
  send_time_preference?: SendTimePreference;
  message_template: string;
  personalization_fields?: string[];
  is_active: boolean;
  priority: number;
  max_sends_per_customer?: number;
  cooldown_days?: number;
  created_by: string;
  created_at: string;
  updated_by?: string;
  updated_at: string;
  starts_at?: string;
  expires_at?: string;
}

// Trigger rule conditions structure
export interface TriggerRuleConditions {
  conditions: Record<string, TriggerCondition>;
  logic: LogicOperator;
}

// Individual condition definition
export interface TriggerCondition {
  operator: ConditionOperator;
  value: any; // Can be string, number, array, etc.
  field_type?: 'number' | 'string' | 'array' | 'date';
}

// Time delay configuration
export interface TimeDelay {
  value: number;
  unit: TimeUnit;
}

// Send time preference
export interface SendTimePreference {
  preferred_hours?: number[]; // Array of hours (0-23)
  timezone?: string;
  avoid_weekends?: boolean;
  business_hours_only?: boolean;
}

// Form data interface for creating/editing triggers
export interface TriggerFormData {
  rule_name: string;
  rule_description?: string;
  trigger_type: TriggerType;
  conditions: TriggerConditionFormData[];
  logic: LogicOperator;
  time_delay: TimeDelay;
  send_time_preference?: SendTimePreference;
  message_template: string;
  personalization_fields?: string[];
  priority: number;
  max_sends_per_customer?: number;
  cooldown_days?: number;
  starts_at?: string;
  expires_at?: string;
  is_active?: boolean;
}

// Form-friendly condition data
export interface TriggerConditionFormData {
  id: string; // Temporary ID for form management
  field: ConditionField;
  operator: ConditionOperator;
  value: any;
  field_type: 'number' | 'string' | 'array' | 'date';
  isRequired?: boolean; // For conditions that cannot be removed (e.g., days_since_last_order for re_engagement)
}

// Predefined trigger templates
export interface TriggerTemplate {
  id: string;
  name: string;
  description: string;
  category: 'welcome' | 'review' | 'loyalty' | 'retention' | 'milestone';
  trigger_type: TriggerType;
  default_conditions: TriggerConditionFormData[];
  default_logic: LogicOperator;
  default_time_delay: TimeDelay;
  default_message: string;
  personalization_fields: string[];
  default_priority?: number;
  default_max_sends_per_customer?: number | null;
  default_cooldown_days?: number | null;
  use_cases: string[];
}

// Trigger execution history
export interface TriggerExecution {
  id: string;
  trigger_rule_id: string;
  trigger_event_id: string;
  customer_id: string;
  restaurant_id: string;
  organisation_id: string;
  scheduled_for: string;
  executed_at?: string;
  sms_message_id?: number;
  scheduled_message_id?: string;
  status: ExecutionStatus;
  skip_reason?: string;
  message_content?: string;
  message_units?: number;
  cost?: number;
  created_at: string;
}

// Extended execution with customer details for UI
export interface TriggerExecutionWithCustomer extends TriggerExecution {
  customer_name?: string;
  customer_phone?: string;
  customer_email?: string;
  rule_name?: string;
}

// API response interfaces
export interface TriggerListResponse {
  success: boolean;
  triggers: SmsTriggerRule[];
  total_count?: number;
}

export interface TriggerCreateResponse {
  success: boolean;
  data: SmsTriggerRule;
  message?: string;
  error?: string;
}

export interface TriggerUpdateResponse {
  success: boolean;
  data: SmsTriggerRule;
  message?: string;
  error?: string;
}

export interface TriggerExecutionHistoryResponse {
  success: boolean;
  executions: TriggerExecutionWithCustomer[];
  total_count?: number;
  pagination?: {
    page: number;
    per_page: number;
    total_pages: number;
  };
}

// Hook return types
export interface UseTriggersList {
  triggers: SmsTriggerRule[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export interface UseTriggersManagement {
  createTrigger: (data: TriggerFormData) => Promise<SmsTriggerRule>;
  updateTrigger: (id: string, data: Partial<TriggerFormData>) => Promise<SmsTriggerRule>;
  toggleTrigger: (id: string, enabled: boolean) => Promise<SmsTriggerRule>;
  deleteTrigger: (id: string) => Promise<void>;
  loading: boolean;
  error: string | null;
}

export interface UseTriggerTemplates {
  templates: TriggerTemplate[];
  loading: boolean;
  error: string | null;
}

export interface UseTriggerHistory {
  executions: TriggerExecutionWithCustomer[];
  loading: boolean;
  error: string | null;
  pagination?: {
    page: number;
    per_page: number;
    total_pages: number;
  };
  loadPage: (page: number) => Promise<void>;
}

// Validation interfaces
export interface TriggerValidationError {
  field: string;
  message: string;
}

export interface TriggerValidationResult {
  isValid: boolean;
  errors: TriggerValidationError[];
}

// Filter and sort options for trigger list
export interface TriggerListFilters {
  status?: TriggerStatus[];
  trigger_type?: TriggerType[];
  restaurant_id?: string;
  search?: string;
}

export interface TriggerListSortOptions {
  field: 'rule_name' | 'created_at' | 'updated_at' | 'priority';
  direction: 'asc' | 'desc';
}

export interface TriggerListOptions {
  filters?: TriggerListFilters;
  sort?: TriggerListSortOptions;
  page?: number;
  per_page?: number;
}

// Utility types for form steps
export type TriggerFormStep = 
  | 'basic'
  | 'conditions' 
  | 'message'
  | 'timing'
  | 'review';

export interface TriggerFormState {
  currentStep: TriggerFormStep;
  data: TriggerFormData;
  isValid: boolean;
  errors: Record<string, string>;
}

// Constants for UI
export const TRIGGER_TYPE_LABELS: Record<TriggerType, string> = {
  milestone: 'Customer Milestone',
  re_engagement: 'Re-engagement'
};

// Descriptions to help users understand each trigger type
export const TRIGGER_TYPE_DESCRIPTIONS: Record<TriggerType, string> = {
  milestone: 'Send messages based on customer activity and milestones (e.g., first order, nth order, spending thresholds)',
  re_engagement: 'Send messages to customers who haven\'t ordered in a specified time period'
};

export const TRIGGER_STATUS_LABELS: Record<TriggerStatus, string> = {
  active: 'Active',
  paused: 'Paused',
  draft: 'Draft',
  expired: 'Expired'
};

export const EXECUTION_STATUS_LABELS: Record<ExecutionStatus, string> = {
  pending: 'Pending',
  scheduled: 'Scheduled',
  sent: 'Sent',
  failed: 'Failed',
  skipped: 'Skipped',
  cancelled: 'Cancelled'
};

export const CONDITION_FIELD_LABELS: Record<ConditionField, string> = {
  total_orders: 'Total Orders',
  total_spend: 'Total Spend',
  average_order_value: 'Average Order Value',
  days_since_last_order: 'Days Since Last Order',
  service_type: 'Service Type',
  loyalty_tier: 'Loyalty Tier'
};

export const OPERATOR_LABELS: Record<ConditionOperator, string> = {
  equals: 'Equals',
  not_equals: 'Not Equals',
  greater_than: 'Greater Than',
  less_than: 'Less Than',
  greater_than_or_equal: 'Greater Than or Equal',
  less_than_or_equal: 'Less Than or Equal',
  between: 'Between',
  in: 'In',
  not_in: 'Not In',
  contains: 'Contains',
  not_contains: 'Does Not Contain'
};

export const TIME_UNIT_LABELS: Record<TimeUnit, string> = {
  minutes: 'Minutes',
  hours: 'Hours',
  days: 'Days',
  weeks: 'Weeks'
};